import { Component } from '@theme/component';

/**
 * <carousel-autoplay> — timed one-card auto-advance for Horizon resource-list product rails.
 *
 * Horizon's own `slideshow-component[autoplay]` is deliberately NOT used. Five faults, all
 * verified (see docs/superpowers/specs/2026-07-15-carousel-autoplay-design.md):
 *
 *   1. Its pause/play control binds `on:click="/play"`. The delegate (component.js) invokes the
 *      method with the click event as its first argument, so `play(interval = this.autoplayInterval)`
 *      receives a MouseEvent, the default parameter never applies, and `setInterval` coerces it to
 *      NaN and fires at ~1ms. Measured: five ticks in six milliseconds.
 *   2. It page-steps (`nextIndex = current + visibleSlides.length`), not card-steps.
 *   3. It rewinds to index 0 at the end, regardless of the `infinite` attribute.
 *   4. No `prefers-reduced-motion` gate — `play()` never checks. Reduced motion only makes
 *      `select()` instant, so those users get hard teleports every tick rather than no motion.
 *   5. No in-viewport gate — `resume()` runs in `connectedCallback` and `play()` only skips on
 *      `:hover` (absent on touch) and `document.hidden`, so an ungated rail ticks while far
 *      off-screen and is parked mid-list by the time it is scrolled to.
 *
 * Instead this scrolls the native `slideshow-slides` scroll-snap rail directly, exactly one card
 * pitch per tick, ping-ponging at each end. The controller shape (pause-reasons Set, viewport
 * gate, live reduced-motion listener) mirrors assets/powerlete-cart-upsell.js, which already runs
 * an autoplaying product rail in this theme. slideshow.js is not touched.
 *
 * Mutually exclusive with <carousel-loop> (enforced in the section schema): a scrollBy tick and
 * carousel-loop's normalise-at-rest would fight over scrollLeft.
 */

/** Fraction of the rail that must be on screen before autoplay runs. */
const VISIBILITY_RATIO = 0.35;

/** Floor on the merchant-set interval, so a bad setting can never spin the rail. */
const MIN_INTERVAL_MS = 1000;

/** Fallback interval (s) when the attribute is missing or unparseable. */
const DEFAULT_INTERVAL_S = 4;

class CarouselAutoplay extends Component {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLElement | null} The inner slideshow-component that owns the rail. */
  #slideshow = null;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer = undefined;

  /** @type {1 | -1} */
  #direction = 1;

  /** @type {number} */
  #intervalMs = DEFAULT_INTERVAL_S * 1000;

  /**
   * Reasons currently holding autoplay off. A SET, not a boolean: several reasons can apply at
   * once (off-screen AND reduced motion AND paused by the control), so one clearing must not
   * restart the rail while another still holds. Autoplay runs only when the set is empty.
   * Starts holding 'hidden' — proven visible before it ever moves.
   * @type {Set<string>}
   */
  #pauseReasons = new Set(['hidden']);

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {MediaQueryList | null} */
  #motionQuery = null;

  /**
   * Scopes every listener so they can be torn off in one call.
   *
   * Recreated on each #init(), NOT once at field-initialiser time: an aborted signal is aborted
   * forever, so a element that is disconnected and re-inserted (morph re-renders the section on
   * cart updates) would re-run #init() and silently attach nothing — autoplay would look alive but
   * never respond to pointer, focus, or motion changes again.
   * @type {AbortController}
   */
  #listeners = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    // Defer a frame so the inner <slideshow-component> has upgraded and laid out.
    requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  #teardown() {
    this.#suspend();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#listeners.abort();
  }

  #init() {
    if (!this.isConnected) return;

    // Re-entrant safe: a re-insertion runs connectedCallback again, so drop the old wiring first.
    this.#teardown();
    this.#listeners = new AbortController();
    this.#pauseReasons = new Set(['hidden']);

    const scroller = this.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    this.#scroller = scroller;
    const slideshow = this.querySelector('slideshow-component');
    this.#slideshow = slideshow instanceof HTMLElement ? slideshow : null;

    // Nothing to advance through.
    if (this.#slides().length < 2) return;

    const seconds = parseFloat(this.getAttribute('interval') ?? '');
    this.#intervalMs = Math.max(
      MIN_INTERVAL_MS,
      (Number.isFinite(seconds) ? seconds : DEFAULT_INTERVAL_S) * 1000
    );

    const { signal } = this.#listeners;

    // Viewport gate (fault 5 above).
    //
    // Observe the SCROLLER, never `this`: this element is `display: contents` (so the rail lays out
    // exactly as it would without the wrapper), which means it generates no layout box at all, and
    // an IntersectionObserver on a boxless element reports intersectionRatio 0 forever — autoplay
    // would never start. carousel-loop.js sidesteps the same trap by watching slideshow.js's
    // [in-viewport] attribute rather than observing itself.
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry && entry.intersectionRatio >= VISIBILITY_RATIO) this.#clear('hidden');
        else this.#hold('hidden');
      },
      { threshold: [0, VISIBILITY_RATIO] }
    );
    this.#visibilityObserver.observe(scroller);

    // Reduced motion (fault 4): never autoplay, and keep watching in case it is toggled mid-session.
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const applyMotion = () => {
      if (this.#motionQuery?.matches) this.#hold('motion');
      else this.#clear('motion');
    };
    this.#motionQuery.addEventListener('change', applyMotion, { signal });
    applyMotion();

    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.#hold('tab') : this.#clear('tab')),
      { signal }
    );

    // Shopper intent — permanent for the pageview, because once someone is working the rail by
    // hand autoplay must never move a card out from under them. Mirrors the 'picker' reason in
    // powerlete-cart-upsell.js.
    //
    // Two constraints shape this:
    //
    // 1. It must NOT come from the rail's `scroll` event. Our own scrollBy tick emits `scroll`
    //    too, so a scroll-based detector would set 'user' on the very first tick and autoplay
    //    would stop dead after a single card.
    // 2. It must be HORIZONTAL intent only. The rail is full-width, so scrolling the page past it
    //    (wheel on desktop, swipe on mobile) is the normal way to move down the homepage and
    //    lands squarely on the rail. Treating any contact as engagement killed autoplay for
    //    essentially every visitor on their first scroll — the feature would never be seen.
    //
    // Vertical gestures pass straight through; only a sideways drag or a sideways wheel counts.
    this.#watchForHorizontalIntent(scroller, signal);

    // Keyboard users tabbing through the CARDS must not have the rail move under them either.
    // Unlike 'user' this clears again, since focus leaving means they have moved on.
    //
    // Scoped to the scroller, NOT to `this`: the autoplay pause/play buttons live inside this
    // element but outside slideshow-slides (snippets/slideshow.liquid renders {{ controls }} after
    // slideshow-container). Listening on the host would mean clicking Play focuses Play, which
    // fires focusin, which re-holds 'focus' — so play() could never resume and the control would
    // look broken to exactly the keyboard users it exists for (WCAG 2.2.2).
    scroller.addEventListener('focusin', () => this.#hold('focus'), { signal });
    scroller.addEventListener('focusout', (event) => {
      // Tabbing from one card to the next fires focusout then focusin. Without this guard the rail
      // would resume and re-pause between every pair of cards, flickering the pause/play icon.
      const next = /** @type {FocusEvent} */ (event).relatedTarget;
      if (next instanceof Node && scroller.contains(next)) return;
      this.#clear('focus');
    }, { signal });

    // Mouse hover — desktop only. `mouseenter`/`mouseleave` never fire from touch input (no
    // synthetic mouse events on a tap-to-scroll rail), so this cannot re-trigger the touch-scroll
    // problem #watchForHorizontalIntent's 8px threshold exists to avoid: a plain finger swipe down
    // the page never fires `mouseenter`. Matches slideshow.js's own `mouseenter`/`mouseleave` ->
    // suspend/resume pair (Horizon's native carousel) — this component is the one place that
    // convention was missing. Without it, a tick can land while the pointer is resting on a card,
    // yanking it out from under the shopper mid-look.
    //
    // Deliberately separate from 'user': resting the mouse is not the same signal as grabbing the
    // rail. Hover clears the moment the pointer leaves, same as 'focus'; 'user' never clears.
    scroller.addEventListener('mouseenter', () => this.#hold('hover'), { signal });
    scroller.addEventListener('mouseleave', () => this.#clear('hover'), { signal });
  }

  /**
   * Hold 'user' on the first sign the shopper is moving the rail sideways.
   *
   * Touch and pointer are handled separately on purpose: during a native touch scroll the browser
   * takes the gesture over and fires pointercancel, so pointermove deltas are unreliable on
   * mobile — touchmove still reports them. Mouse drags have no touch events, so they need the
   * pointer pair.
   *
   * @param {HTMLElement} scroller
   * @param {AbortSignal} signal
   */
  #watchForHorizontalIntent(scroller, signal) {
    /** Movement (px) before a gesture's direction is judged; below this it is still a tap. */
    const INTENT_PX = 8;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    /**
     * @param {number} x
     * @param {number} y
     */
    const begin = (x, y) => {
      startX = x;
      startY = y;
      tracking = true;
    };

    /**
     * @param {number} x
     * @param {number} y
     */
    const judge = (x, y) => {
      if (!tracking) return;
      const dx = Math.abs(x - startX);
      const dy = Math.abs(y - startY);
      if (dx < INTENT_PX && dy < INTENT_PX) return; // still ambiguous, keep watching
      tracking = false;
      // Sideways wins → they are working the rail. Vertical → they are scrolling the page; leave
      // autoplay alone.
      if (dx > dy) this.#hold('user');
    };

    scroller.addEventListener('touchstart', (event) => {
      const touch = /** @type {TouchEvent} */ (event).touches[0];
      if (touch) begin(touch.clientX, touch.clientY);
    }, { passive: true, signal });

    scroller.addEventListener('touchmove', (event) => {
      const touch = /** @type {TouchEvent} */ (event).touches[0];
      if (touch) judge(touch.clientX, touch.clientY);
    }, { passive: true, signal });

    scroller.addEventListener('pointerdown', (event) => {
      const e = /** @type {PointerEvent} */ (event);
      if (e.pointerType === 'touch') return; // touchstart/touchmove own this case
      begin(e.clientX, e.clientY);
    }, { passive: true, signal });

    scroller.addEventListener('pointermove', (event) => {
      const e = /** @type {PointerEvent} */ (event);
      if (e.pointerType === 'touch') return;
      judge(e.clientX, e.clientY);
    }, { passive: true, signal });

    for (const type of ['pointerup', 'pointercancel', 'touchend', 'touchcancel']) {
      scroller.addEventListener(type, () => { tracking = false; }, { passive: true, signal });
    }

    // Trackpads and horizontal-capable mice: sideways wheel is unambiguous rail intent, while a
    // plain vertical wheel over the rail is just the shopper scrolling the page.
    scroller.addEventListener('wheel', (event) => {
      const e = /** @type {WheelEvent} */ (event);
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) this.#hold('user');
    }, { passive: true, signal });
  }

  /**
   * The rail's own slides, read live rather than cached.
   *
   * Cached at init they go stale the moment morph re-renders the section (cart updates re-render
   * sections), leaving detached nodes whose getBoundingClientRect() is all zeroes — #pitch() would
   * return 0 and autoplay would silently stop advancing while still looking active.
   * Direct children only, so the nested per-card image-gallery slides are excluded.
   * @returns {HTMLElement[]}
   */
  #slides() {
    if (!this.#scroller) return [];
    return /** @type {HTMLElement[]} */ (
      Array.from(this.#scroller.children).filter((el) => el.tagName === 'SLIDESHOW-SLIDE')
    );
  }

  /**
   * Stop autoplay. Public — bound from the control markup via
   * `on:click="carousel-autoplay/pause"` (see snippets/slideshow-controls.liquid).
   *
   * Takes NO parameter. Horizon's delegate invokes bound methods with the click event as the first
   * argument, which is exactly what breaks slideshow.js's `play(interval = this.autoplayInterval)`
   * — the MouseEvent lands in `interval` and setInterval fires at ~1ms. Our interval lives in
   * #intervalMs and is read only inside #run(), so no event can ever reach a timer.
   */
  pause() {
    this.#hold('control');
    this.#syncToggle();
  }

  /** Resume autoplay. Public — see pause() for why this takes no parameter. */
  play() {
    this.#clear('control');
    this.#syncToggle();
  }

  /**
   * Flip between paused and playing. Public — bound from the visually-hidden control via
   * `on:click="carousel-autoplay/toggle"`.
   *
   * One toggle rather than the separate pause/play pair slideshow-controls uses: that pair relies on
   * CSS to show one and hide the other, which cannot work for a control that is itself hidden until
   * focused. Takes no parameter — see pause().
   */
  toggle() {
    if (this.#pauseReasons.has('control')) this.#clear('control');
    else this.#hold('control');
    this.#syncToggle();
  }

  /**
   * Keep the hidden control's accessible name and pressed state truthful.
   *
   * Only reflects the 'control' reason, not the whole paused state: a rail paused because it is
   * off-screen or because the shopper grabbed it is not "paused by this button", and saying so would
   * mislead a screen-reader user about what pressing it does.
   */
  #syncToggle() {
    const button = this.refs.autoplayToggle;
    if (!(button instanceof HTMLElement)) return;
    const pausedByControl = this.#pauseReasons.has('control');
    button.setAttribute('aria-pressed', String(pausedByControl));
    const label = pausedByControl ? this.dataset.playLabel : this.dataset.pauseLabel;
    if (label) button.textContent = label;
  }

  /**
   * Gap-inclusive card pitch from live geometry, so the step lands on the layout's scroll-snap
   * points at any breakpoint (card widths here are container-query driven).
   * @returns {number}
   */
  #pitch() {
    const [a, b] = this.#slides();
    if (!a || !b) return 0;
    return Math.round(b.getBoundingClientRect().left - a.getBoundingClientRect().left);
  }

  /**
   * Add a pause reason and stop the rail.
   * @param {string} reason
   */
  #hold(reason) {
    this.#pauseReasons.add(reason);
    this.#suspend();
    this.toggleAttribute('paused', true);
  }

  /**
   * Drop a pause reason; autoplay resumes only when none remain.
   *
   * NOTE: 'user' is deliberately never cleared by any caller — that is what makes shopper
   * engagement permanent for the pageview. Do not add a #clear('user') call.
   *
   * @param {string} reason
   */
  #clear(reason) {
    this.#pauseReasons.delete(reason);
    if (this.#pauseReasons.size === 0) this.#run();
  }

  #run() {
    if (this.#timer || !this.#scroller) return;
    this.toggleAttribute('paused', false);

    // Mark the rail 'actioned', exactly as slideshow.js does on pointerenter. This forces the
    // peeking product cards to `content-visibility: visible` (see resource-list-carousel.liquid's
    // `slideshow-component[actioned] &` rule). Without it, peeking cards stay at
    // `content-visibility: auto` — skipped from paint while off-screen — and POP into paint as the
    // autoplay scroll slides them into view, which reads as a flicker on the entering/exiting card.
    // User-driven scroll never flickers precisely because pointerenter sets this; autoplay bypasses
    // pointer events, so it must set it itself. The attribute only controls paint eagerness (no
    // behavioural side effects — it is read in 5 CSS rules and nowhere in JS).
    this.#slideshow?.setAttribute('actioned', '');

    this.#timer = setInterval(() => this.#tick(), this.#intervalMs);
  }

  #suspend() {
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #tick() {
    const scroller = this.#scroller;
    if (!scroller) return;

    const pitch = this.#pitch();
    if (pitch <= 0) return;

    // Flip BEFORE stepping when the next step would clamp against an edge, so a tick is never
    // wasted scrolling into a wall. The loop is off on these rails, so the rail has real ends;
    // ping-pong keeps it alive without the long visible rewind Horizon's own autoplay does.
    // Tolerance absorbs sub-pixel mandatory-snap correction.
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const atEnd = scroller.scrollLeft >= maxScroll - 2;
    const atStart = scroller.scrollLeft <= 2;

    if (this.#direction === 1 && atEnd) this.#direction = -1;
    else if (this.#direction === -1 && atStart) this.#direction = 1;

    scroller.scrollBy({ left: this.#direction * pitch, behavior: 'smooth' });
  }
}

if (!customElements.get('carousel-autoplay')) {
  customElements.define('carousel-autoplay', CarouselAutoplay);
}
